import Loading from "@/src/components/Loading";
import { Button } from "@/src/components/tailwind/Button";
import { TextArea } from "@/src/components/tailwind/TextArea";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { toasts } from "@/src/components/toasts/toasts";
import {
  AccountDocument,
  AccountQuery,
  AccountQueryVariables,
  useDealQuery,
  useUpdateDealMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { actions } from "@/src/store/auth/slice";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { SettingsCard, SettingsPage } from "@/src/components/settings/SettingsLayout";

export function CompanyDetails() {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const client = useGqlClient();

  const updateDeal = useUpdateDealMutation(client);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");

  const dealQuery = useDealQuery(
    client,
    {
      id: activeDealId,
    },
    {
      enabled: activeDealId !== "",
    },
  );

  useEffect(() => {
    if (dealQuery.data) {
      setName(dealQuery.data.deal.company.name);
      setWebsite(dealQuery.data.deal.company.website);
      setDescription(dealQuery.data.deal.company.description);
    }
  }, [dealQuery.data]);

  if (dealQuery.error) {
    return (
      <p className="text-sm text-muted-foreground">
        Error loading company details
      </p>
    );
  }

  if (dealQuery.isLoading || !dealQuery.data) {
    return <Loading />;
  }

  return (
    <SettingsPage title="Details" description="Basic information">
      <SettingsCard title="" description="" contentClassName="px-5 py-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateDeal.mutate(
              {
                input: {
                  id: activeDealId ?? "",
                  company: {
                    name,
                    website,
                    description,
                  },
                },
              },
              {
                onSuccess: () => {
                  toasts.success("Company details updated");
                  queryClient.invalidateQueries({
                    queryKey: ["Deal", { id: activeDealId ?? "" }],
                  });
                  client
                    .request<
                      AccountQuery,
                      AccountQueryVariables
                    >(AccountDocument)
                    .then((res) => {
                      if (res.account) {
                        dispatch(
                          actions.refreshAccount({ account: res.account }),
                        );
                      }
                    });
                },
                onError: () => {
                  toasts.error("Failed to update company details");
                },
              },
            );
          }}
          className="w-full max-w-md space-y-1"
        >
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />

          <div className="mt-3 flex items-center leading-6 justify-between">
            <label
              htmlFor="description"
              className="block text-sm font-semibold text-foreground"
            >
              Description
            </label>
            <span className="text-xs text-muted-foreground">Optional</span>
          </div>
          <TextArea
            label="Description"
            value={description}
            name="description"
            placeholder="Enter a description for the deal..."
            onChange={(e) => setDescription(e.currentTarget.value)}
            rows={3}
          />

          <TextInput
            label="Website"
            value={website}
            onChange={(e) => setWebsite(e.currentTarget.value)}
            placeholder="https://example.com"
            secondaryLabel="Optional"
            margin="m 0 0 0"
          />

          <div className="mt-8 flex items-center justify-end">
            <Button text="Save" variant="positive" />
          </div>
        </form>
      </SettingsCard>
    </SettingsPage>
  );
}
